/* eslint-disable react-refresh/only-export-components */

import { createBrowserRouter } from 'react-router';
import { Suspense } from 'react';
import { Layout } from '@components/layout';
import { appRoutes } from '@utils/consts';
import { DefaultView } from '@pages/defaultView';
import { LangValidation } from '@components/langValidation/langValidation';
import { useFilters } from '@hooks/useFilters';
import { useTitle } from '@hooks/useTitle';
import { Loader } from '@components/loader';
import { signUpRoutes } from '@pages/signIn/routes';
import { jobsRoutes } from '@pages/jobs/routes';
import { resourcesRoutes } from '@pages/resources/routes';
import { buildersRoutes } from '@pages/builders/routes';
import { companiesRoutes } from '@pages/companies/routes';
import { inventoryRoutes } from '@pages/inventory/routes';
import { foremanRoutes } from '@pages/foreman/routes';
import { NotFound } from '@pages/notFound';
import { ErrorBoundary } from '@components/errorBoundary';
import { RouteErrorFallback } from '@components/errorBoundary/routeError';
import { mapRoutes } from '@pages/map/routes';
import { hotSeatRoutes } from '@pages/hotSeat/routes';
import { onDeckRoutes } from '@pages/onDeck/routes';
import { workloadRoutes } from '@pages/workload/routes';
import { panelRoutes } from '@pages/panel/routes';
import { dashboardsRoutes } from '@pages/dashboards/routes';
import { oauthRoutes } from '@pages/oauth/routes';
import { useAuth } from '@hooks/auth';
import { useChat } from '@hooks/chat';
import { useImpersonateAuth } from '@hooks/impersonate';
import { useHubspot } from '@hooks/hubspot';
import { useFavicon } from '@hooks/favicon';
import { useCrossTabSync } from '@hooks/crossTabSync';
import { useResumeSync } from '@hooks/resumeSync';
import { startNavigationTrail } from '@utils/navigationTrail';

const MainRoute = () => {
  useFilters();
  useTitle();
  useAuth();
  useChat();
  useImpersonateAuth();
  useHubspot();
  useFavicon();
  useCrossTabSync();
  useResumeSync();

  return (
    <ErrorBoundary>
      <LangValidation>
        <Suspense fallback={<Loader />}>
          <Layout />
        </Suspense>
      </LangValidation>
    </ErrorBoundary>
  );
};

export const router = createBrowserRouter([
  {
    path: '/:lang?',
    element: <MainRoute />,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        path: appRoutes.base,
        element: <DefaultView />,
      },
      {
        path: appRoutes.notFound,
        element: <NotFound />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
      ...signUpRoutes,
      ...oauthRoutes,
      ...hotSeatRoutes,
      ...onDeckRoutes,
      ...workloadRoutes,
      ...mapRoutes,
      ...jobsRoutes,
      ...resourcesRoutes,
      ...buildersRoutes,
      ...companiesRoutes,
      ...inventoryRoutes,
      ...dashboardsRoutes,
      ...panelRoutes,
      ...foremanRoutes,
    ],
  },
]);

startNavigationTrail(router.state.location, (listener) =>
  router.subscribe((state) => listener(state.location, state.historyAction)),
);
